import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiMembership, ApiMembershipProduct, ApiOffer, ApiOfferSet, ApiPaymentSource, ApiUser, MembershipProductsApi, OfferSetsApi, UsersApi } from '@tytapp/api';
import { PageComponent, Redirection } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import { SubSink } from 'subsink';

@Component({
  selector: 'tyt-change-membership-confirmation',
  templateUrl: './change-membership-confirmation.component.html',
  styleUrls: ['./change-membership-confirmation.component.scss']
})
export class ChangeMembershipConfirmationComponent extends PageComponent {
  private userService = inject(UserService);
  private redirection = inject(Redirection);
  private route = inject(ActivatedRoute);
  private userApi = inject(UsersApi);
  private membershipProductsApi = inject(MembershipProductsApi);
  private offerSetsApi = inject(OfferSetsApi);

  @Input() phase = 'loading';
  @Output() phaseChange = new EventEmitter<string>();
  @Input() product: ApiMembershipProduct;
  @Input() periodChange: boolean;
  @Input() newChargeDate: string;
  @Input() user: ApiUser;
  @Output() readyChange = new EventEmitter<boolean>();
  
  membership: ApiMembership;
  products : ApiMembershipProduct[];
  offer: ApiOffer;
  offerSet : ApiOfferSet;
  proratedCost: number;
  prorated: boolean;
  private _changePending = false;
  subsink = new SubSink();

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  async init() {
    this.subsink.add(this.userService.userChanged.subscribe(async user => {
      if (!user || !user.membership) {
        this.userService.goToLogin();
        return;
      }

      if (user) {
        this.user = user;
        this.membership = user.membership;

        let unsupportedGateways = ['google_play', 'iap_apple', 'youtube', 'paypal_agreement', 'paypal_subscription'];
        if (!this.membership || !this.membership.active || unsupportedGateways.includes(this.membership.gateway_name))
          this.redirection.go(`/membership/change`);
      }
    }));

    this.route.paramMap.subscribe(async params => {
      if (params.get('offerSetID')) {
        this.offerSet = await this.offerSetsApi.get(params.get('offerSetID'), false).toPromise();
        if (!this.offerSet) {
          this.redirection.go(`/membership/change`);
          return;
        }

        let offerID = params.get('offerID');
        if (offerID && !offerID.startsWith('p:')) {
          this.offer = this.offerSet.offers.find(x => x.uuid === offerID);
          if (!this.offer) {
            this.redirection.go(`/membership/change`);
            return;
          }
          this.product = this.offer.membership_product;
        } else if (offerID && offerID.startsWith('p:')) {
          let [p, price, period] = offerID.split(':');
          this.pwywPrice = Number(price);
          this.pwywPeriod = period;

          if (!this.offerSet.is_pay_what_you_want) {
            this.redirection.go(`/membership/change`);
          }

        }
        
        if (this.offer)
          await this.updatePreviewForOffer(params.get('offerSetID'), params.get('offerID'));
        else
          await this.updatePreviewForPWYW(params.get('offerSetID'), this.pwywPrice, this.pwywPeriod);
      } else if (params.get('productID')) {
        this.products = await this.membershipProductsApi.all().toPromise();
        this.product = this.products.find(x => x.id === Number(params.get('productID')) || x.product_sku === params.get('productID'));

        if (!this.product) {
          this.redirection.go(`/membership/change`);
        }

        await this.updatePreviewForProduct();
      }

      this.phase = 'confirm';
    });
    
    try {
      this.paymentSource = await this.userApi.getPaymentSource().toPromise();
    } catch (e) {
      console.error(`Failed to retrieve payment source:`);
      console.error(e);
    }
  }
  
  paymentSource: ApiPaymentSource;

  pwywPrice : number;
  pwywPeriod : string;

  get newPeriod() {
    if (this.product)
      return this.product.period;
    return this.pwywPeriod;
  }

  get newName() {
    if (this.product)
      return this.product.name;
    return `Pay What You Want`;
  }
  
  get currentPrice() {
    if (!this.membership)
      return null;
    
    return Number(this.membership.price || this.membership.product.price) / 100;
  }

  get newPriceUSD() {
    return this.newPrice / 100;
  }

  get newPrice() {
    if (this.offer) {
      return (this.offer.price || this.product.price);
    } else if (this.product) {
      return (this.product.price);
    } else if (this.pwywPrice) {
      return this.pwywPrice;
    }

    return null;
  }

  get newFullPriceUSD() {
    return this.newFullPrice / 100;
  }

  /**
   * Get the price the user will pay during their next full renewal (the renewal which is 
   * more than 1 full period out from now). This differs from newPrice when the offer has
   * introductory pricing.
   */
  get newFullPrice() {
    if (this.offer) {
      return this.offer.introductory_periods > 0 
        ? (this.offer.membership_product?.price ?? this.offer.price)
        : this.offer.price;
    } else if (this.product) {
      return this.product.price;
    } else if (this.pwywPrice) {
      return this.pwywPrice;
    }
  }

  async updatePreviewForOffer(offerSetID : string, offerID : string) {    
    try {
      let preview = await this.userApi.previewMembershipChange({
        offer_set_uuid: offerSetID,
        offer_uuid: offerID,
        gateway_name: 'stripe'
      }).toPromise();
      this.proratedCost = preview.cost;
      this.prorated = preview.prorated;
    } catch (e) {
      alert(`Could not prepare a preview for your membership change: ${e.message || e.error || 'An unknown error occurred'}`);
    }
  }

  async updatePreviewForPWYW(offerSetID : string, price : number, period : string) {
    try {
      let preview = await this.userApi.previewMembershipChange({
        offer_set_uuid: offerSetID,
        price, period, gateway_name: 'stripe'
      }).toPromise();
      this.proratedCost = preview.cost;
      this.prorated = preview.prorated;
    } catch (e) {
      alert(`Could not prepare a preview for your membership change: ${e.message || e.error || 'An unknown error occurred'}`);
    }
  };

  async updatePreviewForProduct() {
    try {
      let preview = await this.userApi.previewMembershipChange({
        level_id: this.product.id,
        product_id: this.product.id,
        gateway_name: 'stripe'
      }).toPromise();
      this.proratedCost = preview.cost;
      this.prorated = preview.prorated;
    } catch (e) {
      alert(`Could not prepare a preview for your membership change: ${e.message || e.error || 'An unknown error occurred'}. Please try again later.`);
    }
  }

  setReady(value: boolean) {
    this.readyChange.emit(value);
  }

  setPhase(value: string) {
    this.phaseChange.emit(value);
  }

  perPeriod(period : string) {
    return { monthly: '/month', annually: '/year', one_time: '' }[period];
  }

  periodFriendly(period : string) {
    return period.slice(0, 1).toUpperCase() + period.slice(1);
  }

  async submit() {
    // CRITICAL SECTION: NO ASYNC HERE
    if (this._changePending)
      return;
    this._changePending = true;
    this.phase = 'changing';

    // END CRITICAL SECTION: NO ASYNC HERE

    this.setReady(false);
    this.periodChange = this.newPeriod !== this.membership.product.period ? true: false;

    let result : ApiMembership;

    try {
      if (this.offer) {

        result = await this.userApi.modifyMembership({
          offer_set_uuid: this.offerSet.uuid,
          offer_uuid: this.offer.uuid,
          gateway_name: 'stripe'
        }).toPromise(); 
      } else if (this.product) {
        result = await this.userApi.modifyMembership({
          annual: this.product.period === 'annually',
          level_id: this.product.id,
          product_id: this.product.id
        }).toPromise();
      } else if (this.pwywPrice) {
        result = await this.userApi.modifyMembership({
          offer_set_uuid: this.offerSet.uuid,
          price: this.pwywPrice,
          period: this.pwywPeriod,
          gateway_name: 'stripe'
        }).toPromise();
      }
    } catch(e) {
      this.setReady(true);
      this.phase = 'confirm';
      this.shell.alert(`Uh oh!`, e.message || e.error);
      this.showError(e.message || e.error);
      this._changePending = false;
      return;
    }
    
    Object.assign(this.user.membership, result);
    this.userService.userChanged.next(this.user);

    this.redirection.go('/membership/change/success');
  }

  /**
   * Retrieve the next full, normal renewal date (ie, the one that's more than 1 full period
   * out from now).
   * @returns 
   */
  nextFullRenewalDate() {
    if (!this.user?.membership?.status?.renewal_due_date)
      return undefined;

    let date: Date;

    if (this.periodChange) {
      // Today's date will be the new anchor
      date = new Date();
    } else {
      // Their normal anchor will remain
      date = new Date(this.user.membership.status.renewal_due_date);
    }

    // Note: setMonth()/setYear() will handle out of range values by rolling it over
    // ie setMonth(13) would set year + 1 and month to January

    if (this.newPeriod === 'monthly') {
      date.setMonth(date.getMonth() + 1);
    } else if (this.newPeriod === 'annually') {
      date.setFullYear(date.getFullYear() + 1);
    }

    return date.toISOString();
  }
}
